import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gamepad2, RotateCcw, Trophy, Bot, User } from "lucide-react";
import { EMBED } from "@/constants/testIds";
import AdReward from "./_adReward";

/**
 * Jixxeo TicTacToe — you vs neon AI, best-of-three. Minimax AI with three
 * difficulty tiers (mistake rates).
 */

const LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function winner(b) {
  for (const [a, c, d] of LINES) {
    if (b[a] && b[a] === b[c] && b[a] === b[d]) return { player: b[a], line: [a, c, d] };
  }
  if (b.every(Boolean)) return { player: "draw", line: [] };
  return null;
}

function minimax(b, player, ai, human, depth = 0) {
  const w = winner(b);
  if (w) {
    if (w.player === ai) return { score: 10 - depth };
    if (w.player === human) return { score: depth - 10 };
    return { score: 0 };
  }
  const moves = [];
  for (let i = 0; i < 9; i++) {
    if (b[i]) continue;
    const nb = b.slice(); nb[i] = player;
    const next = minimax(nb, player === ai ? human : ai, ai, human, depth + 1);
    moves.push({ index: i, score: next.score });
  }
  if (player === ai) {
    return moves.reduce((best, m) => (m.score > best.score ? m : best), { score: -Infinity });
  }
  return moves.reduce((best, m) => (m.score < best.score ? m : best), { score: Infinity });
}

const DIFF = [
  { key: "chill", label: "Chill", mistake: 0.6, color: "#39FF14" },
  { key: "hustle", label: "Hustle", mistake: 0.25, color: "#FFE300" },
  { key: "boss", label: "Boss", mistake: 0.05, color: "#FF003C" },
];

export default function JixxeoTicTacToe() {
  const [board, setBoard] = useState(Array(9).fill(""));
  const [turn, setTurn] = useState("X"); // human is X
  const [wLine, setWLine] = useState([]);
  const [status, setStatus] = useState("Your turn");
  const [wins, setWins] = useState({ X: 0, O: 0, draw: 0 });
  const [diff, setDiff] = useState("hustle");
  const [locked, setLocked] = useState(false);

  const active = DIFF.find((d) => d.key === diff);

  const clickCell = (i) => {
    if (locked || board[i] || winner(board)) return;
    const nb = board.slice(); nb[i] = "X";
    setBoard(nb); setTurn("O");
  };

  useEffect(() => {
    const w = winner(board);
    if (w) {
      setWLine(w.line);
      setLocked(true);
      if (w.player === "X") setStatus("You win! ⚡");
      else if (w.player === "O") setStatus("AI wins…");
      else setStatus("Draw");
      setWins((prev) => ({ ...prev, [w.player]: (prev[w.player] || 0) + 1 }));
      return;
    }
    if (turn === "O") {
      // AI move
      const t = setTimeout(() => {
        const empty = board.map((v, i) => (v ? -1 : i)).filter((i) => i >= 0);
        let move;
        if (Math.random() < active.mistake && empty.length) {
          move = empty[Math.floor(Math.random() * empty.length)];
        } else {
          move = minimax(board, "O", "O", "X").index;
        }
        if (move != null && move >= 0) {
          const nb = board.slice(); nb[move] = "O";
          setBoard(nb); setTurn("X");
        }
      }, 450);
      return () => clearTimeout(t);
    }
    setStatus(turn === "X" ? "Your turn" : "AI thinking…");
    setLocked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, turn]);

  const reset = () => {
    setBoard(Array(9).fill("")); setTurn("X"); setWLine([]); setStatus("Your turn"); setLocked(false);
  };
  const resetAll = () => { reset(); setWins({ X: 0, O: 0, draw: 0 }); };

  const cellClass = (i) => {
    const win = wLine.includes(i);
    return "w-full aspect-square rounded-xl border-2 flex items-center justify-center font-heading font-black text-5xl md:text-6xl transition-all";
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-25" style={{
        background: "radial-gradient(ellipse at 70% 20%, rgba(255,0,60,0.15), transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(0,240,255,0.12), transparent 60%)",
      }} />
      <div className="relative max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-xl bg-[#FF003C] flex items-center justify-center shadow-[0_0_24px_rgba(255,0,60,0.5)]">
            <Gamepad2 className="w-6 h-6 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa</div>
            <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none">Jixxeo TicTacToe</h1>
          </div>
        </div>

        {/* Difficulty selector */}
        <div className="flex items-center gap-2 mt-2 mb-6 flex-wrap">
          <span className="mono text-[10px] uppercase tracking-widest text-white/50">// difficulty</span>
          {DIFF.map((d) => (
            <button key={d.key} onClick={() => { setDiff(d.key); resetAll(); }}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${diff === d.key ? "text-black font-bold" : "text-white/60 hover:text-white"}`}
              style={{
                background: diff === d.key ? d.color : "transparent",
                borderColor: diff === d.key ? d.color : "rgba(255,255,255,0.12)",
              }}>
              {d.label}
            </button>
          ))}
        </div>

        {/* Scoreboard */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <ScoreCard icon={<User className="w-4 h-4" />} label="You (X)" value={wins.X} color="#00F0FF" />
          <ScoreCard icon={<Trophy className="w-4 h-4" />} label="Draws" value={wins.draw} color="#FFE300" />
          <ScoreCard icon={<Bot className="w-4 h-4" />} label={`AI (O)`} value={wins.O} color="#FF003C" />
        </div>

        {/* Board */}
        <div className="grid grid-cols-3 gap-2 md:gap-3 max-w-md mx-auto">
          {board.map((v, i) => {
            const win = wLine.includes(i);
            const isX = v === "X"; const isO = v === "O";
            const bg = win ? (isX ? "rgba(0,240,255,0.25)" : isO ? "rgba(255,0,60,0.25)" : "rgba(255,255,255,0.08)") : "rgba(255,255,255,0.03)";
            const border = win ? (isX ? "#00F0FF" : isO ? "#FF003C" : "#FFE300") : "rgba(255,255,255,0.1)";
            const color = isX ? "#00F0FF" : isO ? "#FF003C" : "#fff";
            return (
              <motion.button
                key={i}
                whileTap={{ scale: 0.94 }}
                onClick={() => clickCell(i)}
                disabled={!!v || locked}
                className={cellClass(i)}
                style={{ background: bg, borderColor: border, color, boxShadow: win ? `0 0 30px ${border}55` : "none" }}
              >
                {v}
              </motion.button>
            );
          })}
        </div>

        <div className="mt-6 flex flex-col items-center justify-center gap-3">
          <div className="flex items-center justify-center gap-3">
            <div className="mono text-xs uppercase tracking-widest px-4 py-2 rounded-full border border-white/15 text-white/70">
              {status}
            </div>
            <button data-testid={EMBED.tttReset} onClick={reset}
              className="inline-flex items-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full">
              <RotateCcw className="w-3.5 h-3.5" /> New Round
            </button>
            <button onClick={resetAll}
              className="inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest border border-white/15 text-white/60 hover:text-white px-4 py-2 rounded-full">
              Reset Set
            </button>
          </div>
          {winner(board) && <AdReward game="jixxeo-tictactoe" compact />}
        </div>

        <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-8 text-center">
          minimax ai · three tiers · played entirely in your browser
        </div>
      </div>
    </div>
  );
}

function ScoreCard({ icon, label, value, color }) {
  return (
    <div className="glass rounded-xl p-3 flex items-center gap-2 border" style={{ borderColor: `${color}30` }}>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}20`, color }}>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="mono text-[9px] uppercase tracking-widest text-white/50 truncate">{label}</div>
        <div className="font-heading font-black text-2xl leading-none" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}
